/**
 * Account & Profile Articles — Spanish
 */

import type { HelpArticle } from "../../../types";

export const ACCOUNT_ARTICLES_ES: HelpArticle[] = [
  {
    id: "acc-001",
    slug: "managing-your-profile",
    title: "Gestión de tu perfil",
    description: "Actualiza tu información personal y preferencias",
    category: "account",
    order: 1,
    keywords: ["perfil", "ajustes", "personal", "información", "actualizar"],
    content: `
# Gestión de tu perfil

Mantén tu información de perfil actualizada para transacciones fluidas y cumplimiento normativo.

## Acceder a los ajustes del perfil

1. Haz clic en tu avatar en la barra lateral
2. Selecciona "Ajustes"
3. Ve a la pestaña "Perfil"

## Información editable

**Datos personales**
- Nombre visible (cómo aparece tu nombre)
- Número de teléfono (opcional)
- Idioma preferido

**Dirección**
- Calle y número
- Ciudad
- Estado/Provincia
- Código postal
- País

## Información que no puedes cambiar

Algunos datos quedan bloqueados después de la verificación:
- Nombre legal (contacta con soporte para cambiarlo)
- Fecha de nacimiento
- Nacionalidad
- Datos del documento de identidad

## Actualizar tu dirección

Si te has mudado:
1. Ve a Ajustes > Perfil
2. Haz clic en "Actualizar dirección"
3. Introduce los nuevos datos
4. Puede que necesites reverificar si cambias de país

## Foto de perfil

- Opcional pero recomendada
- Ayuda a identificar tu cuenta
- Debe ser apropiada (sin logos, etc.)

## Preferencias de comunicación

Controla qué notificaciones recibes:
- Actualizaciones de transacciones
- Alertas de seguridad
- Comunicaciones de marketing
- Anuncios de producto

## Exportar datos

Puedes descargar tus datos:
1. Ve a Ajustes > Privacidad
2. Haz clic en "Exportar datos"
3. Recibirás el enlace por correo
4. Disponible en formato JSON
    `.trim(),
  },
  {
    id: "acc-002",
    slug: "changing-email-phone",
    title: "Cambiar correo o teléfono",
    description: "Cómo actualizar tu información de contacto",
    category: "account",
    order: 2,
    keywords: ["correo", "teléfono", "cambiar", "actualizar", "contacto"],
    content: `
# Cambiar correo o teléfono

Tu correo es tu método principal de acceso. Aquí te explicamos cómo actualizarlo de forma segura.

## Cambiar tu correo

### Requisitos
- Acceso al correo actual
- Acceso al correo nuevo
- 2FA si está activado

### Pasos
1. Ve a Ajustes > Seguridad
2. Haz clic en "Cambiar correo"
3. Introduce el nuevo correo
4. Verifica la propiedad del correo actual
5. Verifica la propiedad del correo nuevo
6. Se envía confirmación a ambos

### Notas importantes
- Ambos correos deben ser verificados
- El proceso tarda unos minutos
- El correo antiguo no se puede reutilizar inmediatamente

## Añadir número de teléfono

El teléfono es opcional pero añade seguridad:
1. Ve a Ajustes > Perfil
2. Haz clic en "Añadir teléfono"
3. Introduce el número con código de país
4. Verifica mediante SMS

## Cambiar número de teléfono

1. Ve a Ajustes > Perfil
2. Haz clic en "Cambiar teléfono"
3. Verifica el número actual
4. Introduce y verifica el nuevo número

## ¿Perdiste acceso al correo?

Si no puedes acceder a tu correo:
1. Contacta con soporte inmediatamente
2. Proporciona verificación de identidad
3. Responde a las preguntas de seguridad
4. La recuperación tarda 24-48 horas

## Consideraciones de seguridad

- Los cambios de correo activan alertas de seguridad
- Se revisa el historial de accesos reciente
- Los retiros grandes pueden pausarse
- Puede requerirse verificación adicional
    `.trim(),
  },
  {
    id: "acc-003",
    slug: "account-verification-tiers",
    title: "Niveles de verificación de cuenta",
    description: "Entiende los diferentes niveles de verificación",
    category: "account",
    order: 3,
    keywords: ["verificación", "nivel", "límites", "actualizar", "tier"],
    content: `
# Niveles de verificación de cuenta

Tu nivel de verificación determina tus límites de transacción y las funciones disponibles.

## Niveles de verificación

### Nivel 1: Básico
**Requisitos:**
- Solo verificación de correo

**Límites:**
- Solo consulta
- Sin transacciones

### Nivel 2: Estándar
**Requisitos:**
- Documento de identidad oficial
- Verificación con selfie

**Límites:**
- $50,000 de volumen mensual
- $10,000 por transacción
- Todas las funciones estándar

### Nivel 3: Avanzado
**Requisitos:**
- Estándar + Comprobante de domicilio
- Documentación adicional

**Límites:**
- $500,000 de volumen mensual
- $100,000 por transacción
- Soporte prioritario

### Nivel 4: Enterprise
**Requisitos:**
- Verificación empresarial
- Gestor de cuenta dedicado

**Límites:**
- Límites personalizados
- Acceso a API
- Soporte premium

## Cómo subir de nivel

### De Estándar a Avanzado
1. Ve a Ajustes > Verificación
2. Haz clic en "Subir a Avanzado"
3. Sube comprobante de domicilio:
   - Factura de servicios (< 3 meses)
   - Extracto bancario (< 3 meses)
   - Documento fiscal
4. Espera la revisión (24-48 horas)

### De Avanzado a Enterprise
Contacta con nuestro equipo de ventas en enterprise@supabank.xyz

## Consultar tu nivel

Ver tu nivel actual:
1. Ve a Ajustes > Verificación
2. Mira la insignia "Nivel actual"
3. Consulta los límites restantes

## Comparativa de niveles

| Función | Básico | Estándar | Avanzado | Enterprise |
|---------|--------|----------|----------|------------|
| Depósitos | ❌ | ✅ | ✅ | ✅ |
| Retiros | ❌ | ✅ | ✅ | ✅ |
| Acceso API | ❌ | ❌ | ✅ | ✅ |
| Soporte prioritario | ❌ | ❌ | ✅ | ✅ |
| Gestor dedicado | ❌ | ❌ | ❌ | ✅ |
    `.trim(),
  },
  {
    id: "acc-004",
    slug: "closing-your-account",
    title: "Cerrar tu cuenta",
    description: "Cómo cerrar tu cuenta de Supabank",
    category: "account",
    order: 4,
    keywords: ["cerrar", "eliminar", "cuenta", "cancelar", "baja"],
    content: `
# Cerrar tu cuenta

Si necesitas cerrar tu cuenta, esto es lo que debes saber.

## Antes de cerrar

Asegúrate de:
- Todas las transacciones pendientes estén completadas
- Los fondos hayan sido retirados
- No haya problemas o disputas pendientes
- Hayas descargado los registros que necesites

## Proceso de cierre

1. Ve a Ajustes > Cuenta
2. Baja hasta "Cerrar cuenta"
3. Haz clic en "Solicitar cierre de cuenta"
4. Confirma tu identidad
5. Selecciona el motivo del cierre
6. Confirma el cierre

## Qué sucede después

**Inmediatamente:**
- Se revoca el acceso a la cuenta
- No se pueden hacer nuevas transacciones

**En 30 días:**
- Revisión de retención de datos
- Procesamiento de saldos restantes
- Generación de extractos finales

**Permanente:**
- Cuenta eliminada permanentemente
- No se puede recuperar
- El correo no se puede reutilizar durante 90 días

## Retención de datos

Conservamos algunos datos según lo requiere la ley:
- Registros de transacciones (7 años)
- Documentos de identidad (5 años)
- Registros de cumplimiento (variable)

## Obtener tus fondos

Antes de cerrar:
1. Revisa todos los saldos
2. Inicia el retiro a tu banco
3. Espera a que se complete
4. Luego solicita el cierre

## Reactivación

Una vez cerrada, la cuenta no se puede reactivar. Tendrías que crear una cuenta nueva y completar la verificación de nuevo.

## ¿Preguntas?

Si cierras por algún problema, considera contactar primero con soporte. Puede que podamos resolver tu inquietud.
    `.trim(),
  },
  {
    id: "acc-005",
    slug: "account-recovery",
    title: "Recuperación de cuenta",
    description: "Recupera el acceso a tu cuenta",
    category: "account",
    order: 5,
    keywords: ["recuperación", "acceso", "bloqueado", "ayuda", "soporte"],
    content: `
# Recuperación de cuenta

¿Perdiste el acceso a tu cuenta? Aquí te explicamos cómo recuperarla.

## Problemas de acceso frecuentes

### No recibes el enlace mágico
- Revisa la carpeta de spam
- Verifica que el correo esté bien escrito
- Prueba otro cliente de correo
- Espera unos minutos y reintenta

### El enlace mágico caducó
- Los enlaces caducan en 15 minutos
- Solicita un enlace nuevo
- Comprueba si tienes varios correos

### Cuenta bloqueada
- Demasiados intentos fallidos
- Espera 30 minutos
- O contacta con soporte

## Pasos de recuperación

### Con acceso al correo

1. Ve a la página de inicio de sesión
2. Introduce tu correo
3. Haz clic en "Enviar enlace mágico"
4. Revisa el correo (y spam)
5. Haz clic en el enlace en 15 minutos

### Sin acceso al correo

1. Contacta con support@supabank.xyz
2. Proporciona:
   - Correo de la cuenta
   - Nombre legal completo
   - Fecha de nacimiento
   - Detalles de la última transacción
3. Completa la verificación de identidad
4. Soporte te ayudará con la recuperación

## Verificación de identidad para recuperación

Puede que necesites proporcionar:
- Foto del documento de identidad
- Selfie con nota escrita a mano
- Comprobante de domicilio
- Información específica de la cuenta

## Plazos

| Tipo de recuperación | Tiempo típico |
|---------------------|---------------|
| Inicio de sesión normal | Inmediato |
| Reset de contraseña/2FA | 1-2 horas |
| Recuperación completa | 24-48 horas |

## Consejos de prevención

- Mantén tu correo seguro
- Activa 2FA
- Guarda los códigos de respaldo
- Actualiza tus datos de contacto regularmente
- Usa un gestor de contraseñas para el correo
    `.trim(),
  },
  {
    id: "acc-006",
    slug: "business-accounts",
    title: "Cuentas empresariales",
    description: "Información sobre las funciones para empresas",
    category: "account",
    order: 6,
    keywords: ["empresarial", "corporativo", "empresa", "organización", "negocio"],
    content: `
# Cuentas empresariales

Supabank ofrece cuentas empresariales para compañías que necesitan convertir cripto a fiat a gran escala.

## Ventajas de la cuenta empresarial

- Límites de transacción más altos
- Acceso multiusuario
- Integración por API
- Soporte dedicado
- Informes personalizados
- Funciones de facturación

## Elegibilidad

Las cuentas empresariales están disponibles para:
- Sociedades registradas
- Empresas y asociaciones
- Autónomos (con licencia comercial)
- Organizaciones sin ánimo de lucro

## Documentación requerida

### Documentos de la empresa
- Escritura de constitución
- Estatutos sociales
- Licencia comercial (si aplica)
- Certificado de registro fiscal

### Verificación de propiedad
- Información de beneficiarios finales
- Registro de accionistas
- Identificación de directores

### Verificación de dirección
- Factura de servicios de la empresa
- Extracto bancario
- Contrato de alquiler

## Proceso de solicitud

1. **Regístrate** primero como particular
2. **Completa el KYC personal**
3. **Solicita la cuenta empresarial** en Ajustes
4. **Presenta los documentos de la empresa**
5. **Revisión de verificación** (3-5 días hábiles)
6. **Cuenta activada**

## Acceso multiusuario

Las cuentas empresariales pueden tener:
- Múltiples administradores
- Usuarios de solo lectura
- Flujos de aprobación
- Registros de auditoría de actividad

## Límites empresariales

| Nivel | Volumen mensual | Por transacción |
|-------|-----------------|-----------------|
| Business Estándar | $1,000,000 | $250,000 |
| Business Pro | $5,000,000 | $500,000 |
| Enterprise | Personalizado | Personalizado |

## Acceso a API

Las cuentas empresariales incluyen:
- Acceso a API REST
- Notificaciones webhook
- Entorno sandbox
- Documentación técnica
- Soporte de integración

## Empezar

Contacta con enterprise@supabank.xyz o solicita desde los ajustes de tu cuenta.
    `.trim(),
  },
];
